import Button from "@/components/bricks/button/Button";
import { FormikDatePicker } from "../../formcontrols/DatePicker";
import { FormikTextArea } from "../../formcontrols/TextArea";
import { TextInputFormik } from "../../formcontrols/InputItem";
import { WizardReceipt } from "../common/wizardReceipt";
import { StickyFooter } from "../../stickyFooter/modalStickyFooter";
import { useContext, useEffect } from "react";
import { ModalContext } from "../../Modal/Modal";
import { useBeskikDeponerAdvokat } from "./useBeskikDeponerAdvokat";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import { PersonDto } from "../../../interfaces/schema";
import usePersonCprNumber from "../../../hooks/usePersonCprNumber";
import { cprAdgangInfo, canEditFoedselsDato } from "../../../util/cprAccess";
import Spinner from "../../spinner/Spinner";
import { AlertMessage } from "../../AlertMessage";

interface IBeskikDeponerAdvokatWizardProps {
  person: PersonDto;
  invalidateData: () => void;
}
interface IBeskikDeponerAdvokatWizardWithCprProps extends IBeskikDeponerAdvokatWizardProps {
  cpr: string;
  invalidateCpr: InvalidateDataFunction<any>;
  canReadCpr: boolean;
}

export default function BeskikDeponerAdvokatWizardCprWrapper(
  props: IBeskikDeponerAdvokatWizardProps,
) {
  const { canReadCpr, cpr, cprLoading, invalidateCpr } = usePersonCprNumber(
    props.person.id,
    true,
  );
  if (cprLoading) {
    return (
      <div
        className="c-modal-container d-flex flex-column justify-content-center align-items-center"
        style={{ height: "400px", maxHeight: "calc(100vh - 200px)" }}
      >
        <Spinner size={48} />
      </div>
    );
  }
  // ADV-349: uden CprRead hentes CPR-nummeret ikke; wizarden kører uden løbenummer.
  if (canReadCpr && cpr === undefined) return <p>CPR-kald fejlet</p>;
  return (
    <BeskikDeponerAdvokatWizard
      {...props}
      cpr={cpr ?? ""}
      invalidateCpr={invalidateCpr}
      canReadCpr={canReadCpr}
    />
  );
}

function BeskikDeponerAdvokatWizard({
  cpr,
  invalidateCpr,
  invalidateData,
  person,
  canReadCpr,
}: IBeskikDeponerAdvokatWizardWithCprProps) {
  const { setModalOpen } = useContext(ModalContext);
  const {
    formik,
    beskikDeponeringQuery,
    patchError,
    resetPersonQuery,
    futureFirmaRelationer,
    aktiveFirmaRelationer,
  } = useBeskikDeponerAdvokat(person, cpr, canReadCpr);

  const {
    data,
    error,
    isPending: beskikDeponeringQueryIsLoading,
    reset: resetQuery,
  } = beskikDeponeringQuery;
  const { handleSubmit, values, isSubmitting } = formik;
  const isLoading = isSubmitting || beskikDeponeringQueryIsLoading;
  const { dato } = values;
  if (patchError) {
    return <ReceiptError error={patchError} resetQuery={resetPersonQuery} />;
  }
  if (error) return <ReceiptError error={error} resetQuery={resetQuery} />;
  if (data) {
    return (
      <Receipt
        invalidateData={invalidateData}
        invalidateCpr={invalidateCpr}
        person={person}
        dato={dato}
      />
    );
  }
  return (
    <div className="c-modal-container">
      <form onSubmit={handleSubmit} autoComplete="off">
        <fieldset disabled={isLoading}>
          <div className="c-item-list c-modal-container-inner">
            <h3 className="mb-n2 flex-100">Fødselsdato</h3>
            <div className="flex-100 flex-row align-items-end gap-2">
              <div className="flex-grow-1 position-relative">
                <FormikDatePicker
                  formik={formik}
                  disabled={
                    !canEditFoedselsDato(canReadCpr, {
                      harCprNummer: !!person.harCprNummer,
                      foedselsDato: person.foedselsDato,
                    })
                  }
                  config={{
                    component: "datepicker",
                    id: "foedselsDato",
                    label: canReadCpr
                      ? "Fødselsdato* / CPR-nr."
                      : "Fødselsdato*",
                  }}
                />
                {cprAdgangInfo(canReadCpr, {
                  harCprNummer: !!person.harCprNummer,
                  foedselsDato: person.foedselsDato,
                }) && (
                  <AlertMessage type="info" className="mt-2">
                    {cprAdgangInfo(canReadCpr, {
                      harCprNummer: !!person.harCprNummer,
                      foedselsDato: person.foedselsDato,
                    })}
                  </AlertMessage>
                )}
              </div>
              {canReadCpr && (
                <div className="d-flex align-items-center gap-1">
                  <span>-</span>
                  <TextInputFormik
                    config={{
                      component: "textinput",
                      id: "cprNr",
                      label: " ",
                      placeholder: "0000",
                      initialValue: "",
                    }}
                    formik={formik}
                  />
                </div>
              )}
            </div>
            <h3 className="mb-n2 mt-2">Beskikkelse</h3>
            <div className="flex-100">
              <FormikDatePicker
                formik={formik}
                config={{
                  component: "datepicker",
                  id: "dato",
                  label: "Dato",
                  isRequired: true,
                  initialValue: "",
                }}
              />
            </div>
            <div className="flex-100">
              <FormikTextArea
                formik={formik}
                config={{
                  component: "textarea",
                  id: "aarsag",
                  label: "Årsag",
                  initialValue: "",
                }}
              />
            </div>
            {(futureFirmaRelationer.length > 0 ||
              aktiveFirmaRelationer.length > 1) && (
              <AlertMessage
                warningIcon
                heading="Tjek at firmarelationer på personens stamoplysninger er korrekte
                  efter de er beskikket og deponeret"
              >
                <ul className="fw-medium">
                  {aktiveFirmaRelationer.length > 1 && (
                    <li>
                      {person.fornavne} {person.efternavn} har mere end een
                      eksisterende firmarelation på dato for beskikkelse og
                      deponering..
                    </li>
                  )}

                  {futureFirmaRelationer.length > 0 && (
                    <li className="fw-medium">
                      {person.fornavne} {person.efternavn} har mindst een
                      fremtidig firmarelation i forhold til dato for beskikkelse
                      og deponering.
                    </li>
                  )}
                </ul>
              </AlertMessage>
            )}
          </div>
          <StickyFooter>
            <Button
              color="secondary"
              onClick={() => {
                setModalOpen(false);
              }}
            >
              Annuller
            </Button>
            <Button type="submit" color="primary" disabled={isLoading}>
              Beskik og deponér
            </Button>
          </StickyFooter>
        </fieldset>
      </form>
    </div>
  );
}

const ReceiptError = (props: { error: any; resetQuery: any }) => {
  const { error, resetQuery } = props;
  const errorMsg = (error as any).data?.Message
    ? (error as any).data.Message
    : ((error as any).data ?? null);
  return (
    <div className="c-modal-container">
      <WizardReceipt heading="Noget gik galt" icon="warning">
        <p className="text-muted text-center">
          {errorMsg ? (
            <p className="text-muted text-center">{errorMsg}</p>
          ) : null}
        </p>
      </WizardReceipt>
      <div className="flex-100 d-flex justify-content-center mt-3">
        <Button
          color="secondary"
          onClick={() => {
            resetQuery();
          }}
        >
          Tilbage
        </Button>
      </div>
    </div>
  );
};
const Receipt = (props: {
  invalidateData: () => void;
  invalidateCpr: () => void;
  person: PersonDto;
  dato: Date | undefined;
}) => {
  const { invalidateData, person, dato, invalidateCpr } = props;
  useEffect(() => {
    invalidateCpr();
    invalidateData();
  }, [invalidateData, invalidateCpr]);
  const { setModalOpen } = useContext(ModalContext);
  return (
    <div className="c-modal-container">
      <WizardReceipt
        heading="Beskikkelse og deponering gennemført"
        icon="success"
      >
        {dato && (
          <p className="text-muted text-center">
            {person.fuldtNavn} er beskikket per{" "}
            {format(dato, "PPP", { locale: da })} og samtidig deponeret.
          </p>
        )}
        {!dato && (
          <p className="text-muted text-center">
            {person.fuldtNavn} er beskikket og samtidig deponeret.
          </p>
        )}
      </WizardReceipt>
      <div className="flex-100 d-flex justify-content-center mt-3">
        <Button
          color="secondary"
          onClick={() => {
            setModalOpen(false);
          }}
        >
          Luk
        </Button>
      </div>
    </div>
  );
};
